import React, { useEffect, useState } from "react";
import { useForm, Controller, set } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { cardSchema } from "../helpers/yupSchema";
import moment from "moment";
import ApiService from "../services/ApiService";
import Alert from "react-bootstrap/Alert";

const CardForm = ({ token }) => {
  const YEARS = [];
  const MONTHS = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
  ];

  const populateYearsAndMonths = () => {
    for (let i = 0; i < 25; i++) {
      YEARS.push(moment().get("year") + i);
    }
  };
  populateYearsAndMonths();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(cardSchema),
  });

  const [alert, setAlert] = useState({
    show: false,
    variant: "secondary",
    text: "",
  });
  const [loading, setLoading] = useState(false);
  const [cardType, setCardType] = useState('unknown')
  const [outlets, setOutlets] = useState([])

  const handleCardSubmit = async (data) => {
    try {
      setLoading(true);
      data.token = token;
      let allpayments = await ApiService.getPayments(data);      
      for (const item of allpayments) {
        await ApiService.removePayment({
          token: token,
          id: item.PaymentMethodID,
          outlet: item.Outlet,
        });
        console.log("Removed Payment with id: " + item.PaymentMethodID);
      }

      await ApiService.saveCardPayment(data);
      setLoading(false);
      setAlert({
        show: true,
        variant: "success",
        text: "Thank You! Card details updated successfully.",
      });
      reset({
        name: "",
        email: "",
        cardnumber: "",
        expirymonth: "",
        expiryyear: "",
        zipcode: "",
      });
    } catch (error) {
      console.log(error);
      setLoading(false);
      setAlert({
        show: true,
        variant: "warning",
        text: `Unfortunately, we were unable to update your payment details. A patient coordinator will reach out to you shortly to collect payment details.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const detectCardType = (cardNumber) => {
    // Visa
    if (/^4\d{12}(\d{3})?$/.test(cardNumber)) {
      return setCardType('visa.png')
    }
    // MasterCard
    if (/^5[1-5]\d{14}$/.test(cardNumber)) {
      return setCardType('mastercard.png')
    }
    // American Express
    if (/^3[47]\d{13}$/.test(cardNumber)) {
      return setCardType('amex.png')
    }
    // Discover
    if (/^6(?:011|5[0-9]{2})\d{12}$/.test(cardNumber)) {
      return setCardType('discover.png')
    }
    // JCB
    if (/^(?:2131|1800|35\d{3})\d{11}$/.test(cardNumber)) {
      return setCardType('jcb.jpg')
    }
    // Diners Club
    if (/^3(?:0[0-5]|[68][0-9])\d{11}$/.test(cardNumber)) {
      return setCardType('diners-club.jpg')
    }
    // Add more patterns for other card types as needed

    // If no match
    return setCardType('unknown');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow only numeric input
    const numericValue = value.replace(/\D/g, '');
    e.target.value = numericValue;
  };

  const fetchOutlets = async () => {
    try {
      let outlets = await ApiService.getOutlets();
      setOutlets(outlets);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchOutlets()
  },[])

  useEffect(() => {
    if(outlets.length > 0){
      setValue('outlet', outlets[0].id)
    }
  },[outlets, setValue])

  return (
    <div className="card-form mt-4">
      {alert.show ? (
        <div className="mb-2">
          <Alert
            variant={alert.variant}
            onClose={() => setAlert({ show: false })}
            dismissible
            className="mb-0"
          >
            <p className="mb-0">{alert.text}</p>
          </Alert>
        </div>
      ) : null}
      <form onSubmit={handleSubmit(handleCardSubmit)}>
        <div className="mb-3">
          <Controller
            control={control}
            name="outlet"
            render={({ field }) => (
              <select {...field} className={`form-select ${errors.outlet?.message ? `error` : ``}`}>
                {outlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
        <div className="mb-3">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className={`form-control ${
                  errors.name?.message ? `error` : ``
                }`}
                id="name"
                placeholder="Name on Card"
              />
            )}
          />
        </div>
        <div className="mb-3">
          <Controller
            control={control}
            name="cardnumber"
            render={({ field }) => (
              <div className="cardnumber-wrapper">
                <input
                  {...field}
                  onChange={(e) => {
                    handleInputChange(e)
                    detectCardType(e.target.value)
                    field.onChange(e)
                  }}
                  value={field.value}
                  type="tel"
                  className={`form-control ${
                    errors.cardnumber?.message ? `error` : ``
                  }`}
                  id="cardnumber"
                  placeholder="Card Number"
                  maxLength={16}
                />
                {
                  (cardType !== 'unknown')
                  ?
                    <img src={`/images/${cardType}`} alt="Card Type" />
                  :
                    <img src={`/images/unknown.png`} className="unknown" alt="Card Type" />
                }
              </div>
            )}
          />
        </div>
        <div className="d-flex align-items-center justify-content-between gap-2 card-details-wrapper">
          <div className="d-flex align-items-center mb-3 cardexp gap-2">
            <Controller
              control={control}
              name="expirymonth"
              render={({ field }) => (
                <select
                  {...field}
                  className={`form-select ${
                    errors.expirymonth?.message ? `error` : ``
                  }`}
                >
                  <option value="">MM</option>
                  {MONTHS.map((m) => (
                    <option key={m} value={m < 10 ? `0${m}` : m}>
                      {m < 10 ? `0${m}` : m}
                    </option>
                  ))}
                </select>
              )}
            />
            <Controller
              control={control}
              name="expiryyear"
              render={({ field }) => (
                <select
                  {...field}
                  className={`form-select ${
                    errors.expiryyear?.message ? `error` : ``
                  }`}
                >
                  <option value="">YY</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y.toString().substring(2, 4)}>
                      {y}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
          <div className="mb-3 zipcode">
            <Controller
              control={control}
              name="zipcode"
              render={({ field }) => (
                <input
                  {...field}
                  type="tel"
                  className={`form-control ${
                    errors.zipcode?.message ? `error` : ``
                  }`}
                  onChange={(e) => {
                    handleInputChange(e);
                    field.onChange(e);
                  }}
                  id="zipcode"
                  placeholder="Zip Code"
                  maxLength={5}
                />
              )}
            />
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            disabled={loading}
            className="btn btn-custom-primary"
            type="submit"
          >
            <span className="">Submit</span>
            {loading ? (
              <span
                className="ms-2 spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : null}
          </button>
          <button
            className="btn btn-custom-primary"
            type="reset"
            onClick={reset}
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default CardForm;
